import React, {useMemo} from 'react';
import {SupportedLocales} from '@constants/locales';
import {FeatureLabel} from '@deezer/next/services/types/features';
import {Routes} from '@constants/routes';
import {
	getUserAnalyticsRoute,
	isArtist,
	isArtistManager,
} from '@deezer/next/shared/utils/user';
import {notNull} from '@deezer/next/shared/utils/array';
import {useActiveArtist, useUser} from '@deezer/next/contexts/main/hooks';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import type {BoxProps, IconProps} from '@tempo/core';
import {VStack} from '@tempo/core';
import NavItem from '@components/Sidebar/NavItem';
import {
	BubbleCommentIcon,
	EqualizerIcon,
	GaugeIcon,
	HumanOutlinedIcon,
	HumansIcon,
	NotePlusIcon,
	PlayCircleIcon,
	QuestionIcon,
	SmsIcon,
	StarIcon,
} from '@tempo/icons';
import type {MenuItem} from './types';
import Tooltip from '@components/Tooltip';

type MenuItemExt = MenuItem & {
	icon: React.ElementType<
		Omit<IconProps, 'size'> & {
			size?: 'small' | 'medium';
		}
	>;
};

type MenuItemSection = {
	id: string;
	items: MenuItemExt[];
};

type NavBarProps = {
	onMenuItemClick?: () => void;
	collapsed?: boolean;
} & BoxProps;

const sections = [
	{
		id: '1',
		keys: [FeatureLabel.ANALYTICS_VIEWER, FeatureLabel.ARTISTS_EDITOR],
	},
	{
		id: '2',
		keys: [
			FeatureLabel.CHARTS_VIEWER,
			FeatureLabel.PLAYLISTS_REPORT,
			FeatureLabel.PITCHING_TOOL,
			FeatureLabel.CAMPAIGN_MANAGER,
			FeatureLabel.COPYRIGHT_CLAIM,
		],
	},
	{
		id: 'manage',
		keys: [FeatureLabel.USERS_EDITOR, FeatureLabel.DELIVERY_MONITORING_VIEWER],
	},
];

const NavBox = ({onMenuItemClick, collapsed, ...rest}: NavBarProps) => {
	const {t} = useTranslation('d4c');
	const user = useUser();
	const router = useRouter();
	const activeArtist = useActiveArtist();

	const analyticsRoute = useMemo(() => getUserAnalyticsRoute(user), [user]);

	const navBarSections: MenuItemSection[] = useMemo(() => {
		if (!user || (isArtistManager(user) && !activeArtist)) {
			return [];
		}

		const getFAQURL = () => {
			let suffix;
			switch (router.locale as SupportedLocales) {
				case SupportedLocales.de:
					suffix = '/hc/de';
					break;
				case SupportedLocales.es_ES:
				case SupportedLocales.es:
					suffix = '/hc/es-mx';
					break;
				case SupportedLocales.fr:
					suffix = '/hc/fr-fr';
					break;
				case SupportedLocales.pt_BR:
					suffix = '/hc/pt-br';
					break;
				case SupportedLocales.en_US:
				case SupportedLocales.en:
				case SupportedLocales.it:
				case SupportedLocales.nl:
				case SupportedLocales.ja:
				default:
					suffix = '/hc/en-us';
					break;
			}
			return `https://creatorsupport.deezer.com${suffix}`;
		};

		const menuItems = user.features
			.map(feature => {
				if (!feature.enabled) {
					return undefined;
				}

				switch (feature.label) {
					case FeatureLabel.ANALYTICS_VIEWER:
						return {
							key: FeatureLabel.ANALYTICS_VIEWER,
							label: t('analyticshome_title_analytics_d4c-web'),
							url: analyticsRoute,
							pattern: new RegExp(`^/$|${Routes.ANALYTIC}`, 'i'),
							icon: EqualizerIcon,
						};
					case FeatureLabel.ARTISTS_EDITOR: {
						return {
							key: FeatureLabel.ARTISTS_EDITOR,
							label: t('analyticshome_title_artistprofile_d4c-web'),
							url: isArtist(user)
								? `${Routes.EDIT_ARTIST}/${user.stakeholderId}`
								: isArtistManager(user)
									? `${Routes.EDIT_ARTIST}/${activeArtist}`
									: Routes.EDIT_ARTIST,
							pattern: new RegExp(`^/$|${Routes.EDIT_ARTIST}`, 'i'),
							icon: HumanOutlinedIcon,
						};
					}
					case FeatureLabel.CHARTS_VIEWER:
						return {
							key: FeatureLabel.CHARTS_VIEWER,
							label: t('analyticshome_title_charts_d4c-web'),
							url: Routes.CHARTS,
							pattern: new RegExp(`^/$|${Routes.CHARTS}`, 'i'),
							icon: StarIcon,
						};
					case FeatureLabel.PLAYLISTS_REPORT:
						return {
							key: FeatureLabel.PLAYLISTS_REPORT,
							label: t('playlistreport_title_playlistreport_d4c-web'),
							url: Routes.PLAYLIST_REPORT,
							pattern: new RegExp(`^/$|${Routes.PLAYLIST_REPORT}`, 'i'),
							icon: PlayCircleIcon,
						};
					case FeatureLabel.USERS_EDITOR:
						return {
							key: FeatureLabel.USERS_EDITOR,
							label: t('analyticshome_title_usermanager_d4c-web'),
							url: Routes.USERS,
							pattern: new RegExp(`^/$|${Routes.USERS}`, 'i'),
							icon: HumansIcon,
						};
					case FeatureLabel.DELIVERY_MONITORING_VIEWER:
						return {
							key: FeatureLabel.DELIVERY_MONITORING_VIEWER,
							label: t('analyticshome_title_deliverymonitoring_d4c-web'),
							url: Routes.DELIVERIES,
							pattern: new RegExp(`^/$|${Routes.DELIVERIES}`, 'i'),
							icon: GaugeIcon,
						};
					case FeatureLabel.PITCHING_TOOL:
						return {
							key: FeatureLabel.PITCHING_TOOL,
							label: t('analyticshome_title_pitchingtool_d4c-web'),
							url: Routes.PITCHES,
							pattern: new RegExp(`^/$|${Routes.PITCHES}`, 'i'),
							icon: NotePlusIcon,
						};
					case FeatureLabel.CAMPAIGN_MANAGER:
						return {
							key: FeatureLabel.CAMPAIGN_MANAGER,
							label: t('analyticshome_title_campaignmgr_d4c-web'),
							url: Routes.CAMPAIGNS,
							pattern: new RegExp(`^/$|${Routes.CAMPAIGNS}`, 'i'),
							icon: SmsIcon,
						};
					case FeatureLabel.COPYRIGHT_CLAIM:
						return {
							key: FeatureLabel.COPYRIGHT_CLAIM,
							// fixme translate
							label: 'Copyright claim',
							url: Routes.COPYRIGHT_CLAIMS,
							pattern: new RegExp(`^/$|${Routes.COPYRIGHT_CLAIMS}`, 'i'),
							icon: BubbleCommentIcon,
						};
				}
			})
			.filter(notNull);

		const menuSections = menuItems.reduce<{[key: string]: MenuItemSection}>(
			(acc, item) => {
				const section = sections.find(s => s.keys.includes(item.key));
				const sectionId = section?.id || 'others';

				return {
					...acc,
					[sectionId]: {
						id: sectionId,
						items: [...(acc[sectionId]?.items || []), item],
					},
				};
			},
			{},
		);

		menuSections['manage'] = {
			id: 'manage',
			items: [
				...(menuSections['manage']?.items || []),
				{
					key: 'faq',
					label: 'FAQ',
					url: getFAQURL(),
					externalLink: true,
					icon: QuestionIcon,
				},
			],
		};

		return (
			Object.entries(menuSections)
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				.map(([_, s]) => s)
				.filter(s => s.items.length > 0)
		);
	}, [user, activeArtist, analyticsRoute, router.locale, t]);

	return (
		<VStack spacing="spacing.l" overflow="auto" {...rest}>
			{navBarSections.map(section => (
				<VStack
					key={section.id}
					spacing="spacing.xs"
					alignItems="initial"
					width="100%"
				>
					{section.items.map(({key, ...navItemProps}) => {
						if (collapsed) {
							return (
								<Tooltip
									key={navItemProps.label}
									label={navItemProps.label}
									placement="right"
								>
									<NavItem
										key={key}
										onClick={onMenuItemClick}
										collapsed={collapsed}
										{...navItemProps}
									/>
								</Tooltip>
							);
						} else {
							return (
								<NavItem
									key={key}
									onClick={onMenuItemClick}
									collapsed={collapsed}
									{...navItemProps}
								/>
							);
						}
					})}
				</VStack>
			))}
		</VStack>
	);
};

export default NavBox;
